import React, { useState } from 'react';
import { Form, Input, Button, Card, Typography, App } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../api/axios';
import { useAuth } from '../../contexts/AuthContext';
import { RecaptchaField } from '../../components/common/RecaptchaField';
import type ReCAPTCHA from 'react-google-recaptcha';

const { Title } = Typography;

export const Login: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { login } = useAuth();
    const { message } = App.useApp();
    const recaptchaRef = React.useRef<ReCAPTCHA>(null);

    const onFinish = async (values: any) => {
        setLoading(true);
        try {
            // Manual reCAPTCHA execution for invisible mode (same pattern as Contact.tsx)
            const recaptchaToken = await recaptchaRef.current?.executeAsync();
            if (!recaptchaToken) {
                message.warning('Please complete the reCAPTCHA verification!');
                setLoading(false);
                return;
            }

            const response = await apiClient.post('/auth/login', {
                email: values.email,
                password: values.password,
                recaptchaToken: recaptchaToken
            });

            if (response.data && response.data.succeeded) {
                message.success('Login successful!');
                login(response.data.data.accessToken);
                navigate('/admin');
            } else {
                message.error(response.data.errors?.join(', ') || 'Invalid credentials');
                recaptchaRef.current?.reset();
            }
        } catch (error: any) {
            const errorMsg = error.response?.data?.detail || 'An unexpected error occurred during login.';
            message.error(errorMsg);
            recaptchaRef.current?.reset();
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#f0f2f5' }}>
            <Card style={{ width: 400, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                    <Title level={3}>Mosaik Admin</Title>
                </div>

                <Form
                    name="login_form"
                    initialValues={{ remember: true }}
                    onFinish={onFinish}
                    size="large"
                >
                    <Form.Item
                        name="email"
                        rules={[
                            { required: true, message: 'Please input your Email!' },
                            { type: 'email', message: 'Enter a valid email address!' }
                        ]}
                    >
                        <Input prefix={<UserOutlined />} placeholder="Email" />
                    </Form.Item>

                    <Form.Item
                        name="password"
                        rules={[{ required: true, message: 'Please input your Password!' }]}
                    >
                        <Input.Password prefix={<LockOutlined />} placeholder="Password" />
                    </Form.Item>

                    <Form.Item name="recaptchaToken" className="mb-0">
                        <RecaptchaField ref={recaptchaRef} />
                    </Form.Item>

                    <Form.Item>
                        <Button type="primary" htmlType="submit" style={{ width: '100%' }} loading={loading}>
                            Log in
                        </Button>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
};
